import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { COLORS, Menu } from '@livecommerce/design-system';
import Search from 'components-v3/Search/index.jsx';
import { buildRoute, CLIENT_USAGE } from 'constants/routes.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';

const MAX_ITEMS_SEEN = 7;
const LIST_ITEM_HEIGHT = 44;
const LOOKAHEAD_PADDING = 15;

const Container = styled.div`
  width: 312px;
  padding: 8px;
  color: ${COLORS.dark_1000};
`;

const ClientsListContainer = styled.ul`
  list-style-type: none;
  width: 100%;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  max-height: ${MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING}px;
  min-height: ${(props) => props.minListHeight}px;

  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    height: ${(props) => props.gradientHeight - 8}px;
    z-index: 0;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.68) 50%,
        rgba(255, 255, 255, 0.84) 100%
    );
  }
`;

const organizationsListStyles = css`
  padding: 0;
`;

const StyledSearch = styled(Search)`
  margin: 8px 10px;
`;

const isGradientShown = (numberOfClients, scrollTop) => {
  const itemsOutOfBounds = numberOfClients - MAX_ITEMS_SEEN;

  return itemsOutOfBounds * LIST_ITEM_HEIGHT - LOOKAHEAD_PADDING !== scrollTop;
};

const getClientRoute = ({ clientId }) => {
  return buildRoute(CLIENT_USAGE, { clientId });
};

const ClientsList = ({ isOpen, handleMenuItemClick, clients, ref, ...props }) => {
  const { t } = useLocalization();
  const { selectedProduct } = useProductDataOutsideRoutes();
  const menuRef = useRef(null);
  const [showGradient, setShowGradient] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');

  const clientsArray = Object.entries(clients).map(([id, client]) => ({ id, ...client }));
  const filteredClients = clientsArray.filter(({ title, id }) => {
    if (title?.toLowerCase().includes(searchInputValue.toLowerCase())) return true;
    if (id.toLowerCase().includes(searchInputValue.toLowerCase())) return true;

    return false;
  });

  useEffect(() => {
    const currentRef = menuRef.current;

    const listener = currentRef?.addEventListener('scroll', (event) => {
      const scrollTop = event.target.scrollTop;
      const gradientIsShown = isGradientShown(filteredClients.length, scrollTop);
      setShowGradient(gradientIsShown);
    });

    return () => {
      currentRef?.removeEventListener('scroll', listener);
    };
  }, [filteredClients.length]);

  const handleChange = (event) => {
    const searchString = event.target.value;
    if (searchString) {
      trackingService.track({
        eventType: 'client-switcher:search',
        value: encodeURIComponent(searchString),
      });
    }
    setSearchInputValue(searchString);
  };

  const gradientHeight =
    showGradient && filteredClients.length > MAX_ITEMS_SEEN ? LIST_ITEM_HEIGHT * 2 + LOOKAHEAD_PADDING : 0;

  const minListHeight =
    clientsArray.length >= MAX_ITEMS_SEEN ? MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING : 0;

  const filteredClientsItems = filteredClients.map(({ id, title }) => {
    const to = getClientRoute({ clientId: id, selectedProduct });
    return { to, key: id, label: title || id, action: (e) => handleMenuItemClick({ clientId: id }, e) };
  });

  return (
    <Container ref={ref} {...props}>
      {clientsArray.length >= MAX_ITEMS_SEEN && (
        <StyledSearch width="140px" placeholder={t('genericLabels.searchPlaceholder')} value={searchInputValue} onChange={handleChange} />
      )}
      <ClientsListContainer ref={menuRef} gradientHeight={gradientHeight} minListHeight={minListHeight}>
        <Menu items={filteredClientsItems} minWidth={288} className={organizationsListStyles} />
      </ClientsListContainer>
    </Container>
  );
};

export default React.forwardRef((props, ref) => <ClientsList {...props} ref={ref} />);
